import { FastApiWebSocketAdapter } from "./FastApiWebSocketAdapter";
import { PubNubTransportAdapter } from "./PubNubTransportAdapter";
import {
  ITransportAdapter,
  RealtimeListener,
  RealtimePayload,
  RealtimeStatus,
} from "./types";
import { useAlertsStore } from "@/state/AlertsStore";
import i18n from "@/i18n/i18n";

export interface RealtimeManagerOptions {
  tokenGetter?: () => Promise<string | null>;
  identifier?: string;
}

export type TransportType = "pubnub" | "websocket";

let lastAlertTimestamp = 0;
const ALERT_COOLDOWN_MS = 10000;

export class RealtimeManager implements ITransportAdapter {
  private static instance: RealtimeManager | null = null;
  // Hardcoded toggle for this branch: completely disable PubNub and strictly use FastAPI WebSocket / REST
  private static disablePubNub = true;

  static setDisablePubNubForTesting(disabled: boolean): void {
    RealtimeManager.disablePubNub = disabled;
  }

  static isPubNubDisabled(): boolean {
    return RealtimeManager.disablePubNub;
  }

  private adapter: ITransportAdapter;
  private transportType: TransportType;
  private activeChannels: Set<string> = new Set();
  private withPresence = false;
  private listeners: Set<RealtimeListener> = new Set();
  private identifier: string | null = null;
  private tokenGetter: (() => Promise<string | null>) | null = null;

  constructor(options?: RealtimeManagerOptions) {
    if (options?.tokenGetter) {
      this.tokenGetter = options.tokenGetter;
    }
    if (options?.identifier) {
      this.identifier = options.identifier;
    }

    this.transportType = "websocket";
    this.adapter = new FastApiWebSocketAdapter({
      tokenGetter: options?.tokenGetter,
      onError: (err) => {
        const now = Date.now();
        if (now - lastAlertTimestamp > ALERT_COOLDOWN_MS) {
          lastAlertTimestamp = now;
          useAlertsStore.getState().addAlert({
            level: "Warning",
            message: i18n.t("alerts.wsOfflineFallback"),
            duration: 6000,
          });
        }

        if (!RealtimeManager.disablePubNub) {
          this.fallbackToPubNub(err);
        } else {
          console.warn(
            "RealtimeManager: WebSocket error encountered, but PubNub fallback is disabled for this branch:",
            err,
          );
        }
      },
    });

    if (options?.tokenGetter && this.adapter.setTokenGetter) {
      this.adapter.setTokenGetter(options.tokenGetter);
    }
    if (options?.identifier && this.adapter.setIdentifier) {
      this.adapter.setIdentifier(options.identifier);
    }
  }

  static resolveTransportType(): TransportType {
    return "websocket";
  }

  static getInstance(options?: RealtimeManagerOptions): RealtimeManager {
    if (!RealtimeManager.instance) {
      RealtimeManager.instance = new RealtimeManager(options);
    }
    return RealtimeManager.instance;
  }

  static resetInstance(): void {
    if (RealtimeManager.instance) {
      RealtimeManager.instance.adapter.disconnect?.();
      RealtimeManager.instance = null;
    }
  }

  getAdapter(): ITransportAdapter {
    return this.adapter;
  }

  getTransportType(): TransportType {
    return this.transportType;
  }

  private fallbackToPubNub(error: Error): void {
    if (RealtimeManager.disablePubNub) {
      console.warn(
        "RealtimeManager: PubNub fallback is disabled for this branch. Remaining on WebSocket/REST adapter.",
        error,
      );
      return;
    }

    if (this.transportType === "pubnub") return;

    const errorMsg = `WebSocket connection failed: ${error.message || "Connection error"}. Falling back to PubNub.`;
    console.error("RealtimeManager:", errorMsg);

    this.transportType = "pubnub";
    this.adapter.disconnect?.();

    const pubnubAdapter = new PubNubTransportAdapter();
    if (this.identifier) pubnubAdapter.setIdentifier?.(this.identifier);
    if (this.tokenGetter) pubnubAdapter.setTokenGetter?.(this.tokenGetter);

    // Notify listeners of error
    for (const listener of this.listeners) {
      pubnubAdapter.addListener(listener);
      listener.onStatus?.("error");
    }

    this.adapter = pubnubAdapter;

    if (this.activeChannels.size > 0) {
      this.adapter.subscribe(
        Array.from(this.activeChannels),
        this.withPresence,
      );
    }
  }

  subscribe(channels: string[], withPresence?: boolean): void | Promise<void> {
    for (const ch of channels) {
      this.activeChannels.add(ch.toLowerCase());
    }
    if (withPresence !== undefined) {
      this.withPresence = withPresence;
    }
    return this.adapter.subscribe(channels, withPresence);
  }

  unsubscribe(channels: string[]): void | Promise<void> {
    for (const ch of channels) {
      this.activeChannels.delete(ch.toLowerCase());
    }
    return this.adapter.unsubscribe(channels);
  }

  async publish(channel: string, payload: RealtimePayload): Promise<void> {
    try {
      await this.adapter.publish(channel, payload);
    } catch (e) {
      if (this.transportType === "websocket") {
        if (RealtimeManager.disablePubNub) {
          console.error(
            `RealtimeManager: Failed to publish to channel "${channel}" via WebSocket adapter:`,
            e,
          );
          throw e;
        }
        const err = e instanceof Error ? e : new Error(String(e));
        this.fallbackToPubNub(err);
        await this.adapter.publish(channel, payload);
        return;
      }
      throw e;
    }
  }

  addListener(listener: RealtimeListener): void {
    this.listeners.add(listener);
    this.adapter.addListener(listener);
  }

  removeListener(listener: RealtimeListener): void {
    this.listeners.delete(listener);
    this.adapter.removeListener(listener);
  }

  getPresence(channel: string): Promise<string[]> {
    return this.adapter.getPresence(channel);
  }

  getStatus(): RealtimeStatus {
    return this.adapter.getStatus();
  }

  setIdentifier(id: string): void {
    this.identifier = id;
    this.adapter.setIdentifier?.(id);
  }

  setTokenGetter(getter: () => Promise<string | null>): void {
    this.tokenGetter = getter;
    this.adapter.setTokenGetter?.(getter);
  }

  disconnect(): void {
    this.adapter.disconnect?.();
  }
}

export const realtimeManager = RealtimeManager.getInstance();
